import React from "react";
import { useContent } from "@thoughtbot/superglue";

type TransactionOutcome = "succeeded" | "failed";

type CheckoutContent = {
  paddle?: {
    clientToken: string;
    environment: "production" | "sandbox";
  };
  returnPath?: string;
  // What the provider told the buyer's browser on the way back, already read
  // through an allowlist in CheckoutController. It decides which sentence they
  // read and nothing else — the webhook is what settles the money.
  transactionOutcome?: TransactionOutcome;
};

type PaddleEvent = { name: string };

declare global {
  interface Window {
    Paddle?: {
      Environment: { set(environment: "sandbox"): void };
      Initialize(options: { token: string; eventCallback?: (event: PaddleEvent) => void }): void;
    };
  }
}

function PaddleLoader({
  clientToken,
  environment,
  onEvent,
}: NonNullable<CheckoutContent["paddle"]> & { onEvent: (event: PaddleEvent) => void }) {
  // Paddle keeps the callback it is initialised with, so the latest one has to
  // be reachable through a ref — re-initialising on every render would reopen
  // the overlay.
  const latestOnEvent = React.useRef(onEvent);
  latestOnEvent.current = onEvent;

  React.useEffect(() => {
    const script = document.createElement("script");

    script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
    script.async = true;
    script.onload = () => {
      if (environment === "sandbox") window.Paddle?.Environment.set("sandbox");
      window.Paddle?.Initialize({
        token: clientToken,
        eventCallback: (event) => latestOnEvent.current(event),
      });
    };
    document.head.appendChild(script);

    return () => script.remove();
  }, [clientToken, environment]);

  return null;
}

export default function CheckoutShow() {
  const { paddle, returnPath, transactionOutcome } = useContent<CheckoutContent>();
  // A provider that hosted its own checkout has already sent the buyer back
  // saying how it went, so this page opens on that answer rather than waiting
  // for an overlay event that will never come.
  const [outcome, setOutcome] = React.useState<TransactionOutcome | undefined>(transactionOutcome);
  const paid = outcome === "succeeded";

  // The overlay reports the transaction; the provider's own webhook settles it.
  // Nothing here publishes the invitation — that stays the couple's to do, from
  // the dashboard. Closing an overlay that never completed leaves the buyer
  // here, where the checkout can still open.
  //
  // A refused card is reported too, and is not final: a Paddle transaction
  // survives it, so the buyer can try another card and `checkout.completed`
  // still replaces this with the receipt.
  const handlePaddleEvent = React.useCallback(
    (event: PaddleEvent) => {
      if (event.name === "checkout.completed") setOutcome("succeeded");
      if (event.name === "checkout.payment.failed" || event.name === "checkout.error") setOutcome("failed");
      if (event.name === "checkout.closed" && paid && returnPath) window.location.assign(returnPath);
    },
    [paid, returnPath],
  );

  return (
    <section className="flex flex-1 items-center px-4 py-12 sm:px-6 sm:py-20">
      <div className="mx-auto w-full max-w-3xl rounded-lg border bg-background p-6 shadow-sm sm:p-10">
        <p className="text-sm font-medium text-primary">Goldypaper checkout</p>

        {paid ? (
          <>
            <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight sm:text-5xl">Payment received.</h1>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Thank you. Your invitation is paid for. It is not live yet — publishing is yours to do, whenever you are ready for your guests to see it.
            </p>
            {returnPath ? (
              <p className="mt-6">
                <a
                  className="font-medium text-primary underline decoration-primary/40 underline-offset-4"
                  href={returnPath}
                >
                  Publish from your dashboard
                </a>
              </p>
            ) : (
              <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">
                You can close this page. Sign in to Goldypaper whenever you like, and publish your invitation from your dashboard.
              </p>
            )}
          </>
        ) : outcome === "failed" ? (
          <>
            <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight sm:text-5xl">
              Your transaction did not go through.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              Your card was not charged. This usually means the bank turned the transaction down — trying a different card
              or transaction method is the quickest way through.
            </p>
            {returnPath ? (
              <p className="mt-6">
                <a
                  className="font-medium text-primary underline decoration-primary/40 underline-offset-4"
                  href={returnPath}
                >
                  Try again from your dashboard
                </a>
              </p>
            ) : (
              <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">
                Sign in to Goldypaper and start the transaction again from your dashboard whenever you are ready.
              </p>
            )}
            <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">
              If it keeps happening, write to{" "}
              <a className="font-medium text-primary underline decoration-primary/40 underline-offset-4" href="mailto:contact@goldypaper.com">contact@goldypaper.com</a> and we will sort it out.
            </p>
          </>
        ) : paddle ? (
          <>
            <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight sm:text-5xl">A few more seconds.</h1>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">Opening secure checkout&hellip;</p>
            <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">
              Payment is handled by our merchant of record. If nothing opens, write to{" "}
              <a className="font-medium text-primary underline decoration-primary/40 underline-offset-4" href="mailto:contact@goldypaper.com">contact@goldypaper.com</a>.
            </p>
          </>
        ) : (
          // Nobody arrived here from a checkout. Saying so is the honest answer;
          // the copy this replaced claimed checkout was unavailable, which is a
          // different situation and one this page cannot know about.
          <>
            <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight sm:text-5xl">
              Nothing to pay for here.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              There is no transaction in progress on this page. Your invitations, and the one-time transaction that publishes
              them, live on your dashboard.
            </p>
            <p className="mt-6">
              <a
                className="font-medium text-primary underline decoration-primary/40 underline-offset-4"
                href={returnPath ?? "/dashboard"}
              >
                Go to your dashboard
              </a>
            </p>
          </>
        )}

        {paddle ? <PaddleLoader {...paddle} onEvent={handlePaddleEvent} /> : null}
      </div>
    </section>
  );
}
